<template>
  <div class="payment-container">
    <div class="payment-card">
      <div class="payment-summary">
        <img class="payment-logo" :src="swichLogo" alt="Swich" />
        <h2>Swich Payment Gateway</h2>
        <div v-if="currentInvoice" class="payment-amount">{{ invoiceTotal }} {{ currentInvoice.currency }}</div>
      </div>

      <div class="payment-body">
        <div v-if="isLoading" class="payment-loader">
          <div class="spinner"></div>
          <p>{{ message }}</p>
        </div>

        <template v-else-if="error">
          <h3>Cannot Continue</h3>
          <p>This invoice cannot be paid through Swich.</p>
          <div class="payment-error">{{ error }}</div>
          <div class="payment-actions">
            <button type="button" class="payment-button payment-button-alt" @click="goToInvoices">Back to Invoices</button>
          </div>
        </template>

        <template v-else-if="isPaid">
          <h3>{{ isReturnFromSwich ? "Payment Successful" : "Invoice Already Paid" }}</h3>
          <p>{{ isReturnFromSwich ? "Your payment has been confirmed by Swich." : "This invoice has already been processed and paid." }}</p>
          <div class="payment-info">
            <div class="payment-info-item"><span>Invoice ID</span><strong>{{ currentInvoice.id }}</strong></div>
            <div class="payment-info-item"><span>Customer</span><strong>{{ currentInvoice.customerName }}</strong></div>
            <div class="payment-info-item"><span>Amount</span><strong>{{ invoiceTotal }} {{ currentInvoice.currency }}</strong></div>
            <div class="payment-info-item"><span>Date</span><strong>{{ formatDate(currentInvoice.invoiceDate) }}</strong></div>
            <div class="payment-info-item"><span>Status</span><strong class="status-paid">{{ currentInvoice.status.toUpperCase() }}</strong></div>
            <div v-if="swichReference" class="payment-info-item"><span>Swich reference</span><strong>{{ swichReference }}</strong></div>
          </div>
          <div class="payment-actions">
            <button type="button" class="payment-button payment-button-primary" @click="goToDashboard">Go to Dashboard</button>
          </div>
        </template>

        <template v-else-if="isUnconfirmed">
          <h3>Payment Processing</h3>
          <p>Swich is confirming your payment. This usually takes a few seconds. If the invoice stays unpaid, contact support with your invoice number.</p>
          <div v-if="currentInvoice" class="payment-info">
            <div class="payment-info-item"><span>Invoice ID</span><strong>{{ currentInvoice.id }}</strong></div>
            <div class="payment-info-item"><span>Customer</span><strong>{{ currentInvoice.customerName }}</strong></div>
            <div class="payment-info-item"><span>Amount</span><strong>{{ invoiceTotal }} {{ currentInvoice.currency }}</strong></div>
            <div class="payment-info-item"><span>Status</span><strong>{{ currentInvoice.status.toUpperCase() }}</strong></div>
          </div>
          <div class="payment-actions">
            <button type="button" class="payment-button payment-button-alt" @click="goToInvoices">My Invoices</button>
            <button type="button" class="payment-button payment-button-primary" @click="confirmPayment">Check Status</button>
          </div>
        </template>

        <template v-else-if="landingPageUrl">
          <h3>Payment Details</h3>
          <p>Review the details. You will be taken to Swich to complete the payment.</p>
          <div class="payment-info">
            <div class="payment-info-item"><span>Invoice ID</span><strong>{{ currentInvoice.id }}</strong></div>
            <div class="payment-info-item"><span>Customer</span><strong>{{ currentInvoice.customerName }}</strong></div>
            <div class="payment-info-item"><span>Amount</span><strong>{{ invoiceTotal }} {{ currentInvoice.currency }}</strong></div>
            <div class="payment-info-item"><span>Reference</span><strong>{{ paymentReference }}</strong></div>
            <div v-if="instructions" class="payment-info-item"><span>Pay with</span><strong>{{ instructions }}</strong></div>
          </div>
          <div class="payment-countdown">
            <div class="countdown-circle">{{ countdown }}</div>
            <p>Redirecting to Swich in {{ countdown }} seconds. Don't reload or leave this page.</p>
          </div>
        </template>
      </div>

      <div class="payment-footer">
        <span>Secure payment powered by Swich</span>
        <i class="fas fa-lock"></i>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapState } from "vuex";

export default {
  name: "SwichPayIn",
  props: {
    instructions: { type: String, default: null },
  },
  data() {
    return {
      message: null,
      error: null,
      isLoading: false,
      countdown: 5,
      countdownInterval: null,
      currentInvoice: null,
      landingPageUrl: null,
      paymentReference: null,
      swichReference: null,
      isUnconfirmed: false,
      swichLogo: require("@/assets/images/swich-logo.png"),
    };
  },
  computed: {
    ...mapState("user", ["user"]),
    ...mapState("pricing", ["invoicesByUserId"]),
    currentInvoiceId() {
      return this.$route.params.id;
    },
    isReturnFromSwich() {
      const action = this.$route.query.action;
      return !!action && action.toLowerCase() == "postpayment";
    },
    isPaid() {
      const status = this.currentInvoice && this.currentInvoice.status ? this.currentInvoice.status.toLowerCase() : "";
      return ["paid", "over paid"].includes(status);
    },
    invoiceTotal() {
      if (!this.currentInvoice || !this.currentInvoice.details) {
        return 0;
      }
      return this.currentInvoice.details.reduce((total, detail) => total + detail.amount, 0);
    },
  },
  methods: {
    ...mapActions("pricing", ["fetchInvoiceByUserId", "processPayment", "fetchPaymentsList"]),

    async fetchCurrentInvoice() {
      await this.fetchInvoiceByUserId(this.user.id);
      const invoices = this.invoicesByUserId[this.user.id] || [];
      this.currentInvoice = invoices.find((invoice) => invoice.id == this.currentInvoiceId) || null;

      if (this.isPaid) {
        await this.fetchSwichReference();
      }
    },

    async fetchSwichReference() {
      try {
        const result = await this.fetchPaymentsList({ invoiceID: this.currentInvoiceId });
        const payments = result && result.payload ? result.payload : [];
        const completed = payments.find(
          (payment) => (payment.status || "").toLowerCase() == "completed" && (payment.methodProvider || "").toLowerCase() == "swichpayin"
        );
        this.swichReference = completed && completed.metadata ? JSON.parse(completed.metadata).PaymentReferenceID : null;
      } catch (err) {
        this.swichReference = null;
        this.$toasted.error("Unable to resolve Swich reference.", {
          icon: "exclamation-triangle",
          theme: "outline",
          position: "bottom-center",
          singleton: true,
          duration: 3000,
        });
      }
    },

    async loadInvoice() {
      this.isLoading = true;
      this.message = "Fetching invoice details, please wait...";

      try {
        await this.fetchCurrentInvoice();

        if (!this.currentInvoice) {
          this.error = "Invoice not found.";
        }
      } catch (err) {
        this.error = err?.message || "Failed to load invoice details.";
      } finally {
        this.isLoading = false;
      }
    },

    validateInvoiceForSwich() {
      const currency = (this.currentInvoice.currency || "").toLowerCase();
      if (!["pkr", "usd"].includes(currency)) {
        return `Swich accepts payments in PKR or USD only. This invoice is in ${this.currentInvoice.currency}.`;
      }
      if (!this.user || !this.user.phone) {
        return "A mobile number is required on your profile to pay through Swich. Please update your profile and try again.";
      }
      return null;
    },

    async initiatePayment() {
      const validationError = this.validateInvoiceForSwich();
      if (validationError) {
        this.error = validationError;
        return;
      }

      this.isLoading = true;
      this.message = "Preparing payment gateway, please wait...";

      try {
        const response = await this.processPayment({
          invoiceId: this.currentInvoiceId,
          serviceCode: "SwichPayIn",
          data: {
            redirectUrl: `${window.location.origin}/payment/swichpayin`,
          },
        });

        const landingPageUrl = response && response.data ? response.data.landingPageUrl : null;
        if (!landingPageUrl) {
          this.error = "Some error occurred while fetching data from payment gateway. Please try again or use another payment method.";
          return;
        }

        this.landingPageUrl = landingPageUrl;
        this.paymentReference = response.payment ? response.payment.id : null;
        this.startCountdown();
      } catch (err) {
        this.error = err?.message || "Some error occurred while fetching data from payment gateway.";
      } finally {
        this.isLoading = false;
      }
    },

    startCountdown() {
      this.countdown = 5;

      this.countdownInterval = setInterval(() => {
        this.countdown--;

        if (this.countdown === 0) {
          clearInterval(this.countdownInterval);
          window.location.href = this.landingPageUrl;
        }
      }, 1000);
    },

    async confirmPayment() {
      this.isUnconfirmed = false;
      this.isLoading = true;
      this.message = "Confirming your payment with Swich, please wait...";

      try {
        await this.fetchCurrentInvoice();
        this.isUnconfirmed = !this.isPaid;
      } catch (err) {
        this.error = err?.message || "Failed to confirm the payment. Please check your invoices.";
      } finally {
        this.isLoading = false;
      }
    },

    formatDate(value) {
      if (!value) {
        return "";
      }
      return new Date(value).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
    },

    goToDashboard() {
      this.$router.push("/user/dashboard");
    },

    goToInvoices() {
      this.$router.push("/user/invoices");
    },
  },
  async created() {
    if (!this.currentInvoiceId) {
      this.error = "Invalid parameters";
      return;
    }

    if (this.isReturnFromSwich) {
      this.confirmPayment();
      return;
    }

    await this.loadInvoice();
    if (this.currentInvoice && !this.isPaid) {
      this.initiatePayment();
    }
  },
  beforeDestroy() {
    clearInterval(this.countdownInterval);
  },
};
</script>

<style scoped>
.payment-container {
  --swich-blue: #0030e0;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: calc(100vh - var(--header-height) - var(--footer-height));
  padding: 2rem 1rem;
  color: var(--color-text-default);
}

.payment-card {
  width: 100%;
  max-width: 900px;
  margin: 2rem 0;
  display: grid;
  grid-template-columns: 260px 1fr;
  background: var(--color-white);
  border: 1px solid var(--color-light-gray);
  border-radius: 0.75rem;
  overflow: hidden;
}

.payment-summary {
  position: relative;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.payment-summary::before {
  content: "";
  position: absolute;
  inset: 0;
  background: var(--swich-blue);
  opacity: 0.12;
}

.payment-summary > * {
  position: relative;
}

.payment-logo {
  height: 28px;
  width: auto;
  align-self: flex-start;
}

.payment-summary h2 {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 500;
  color: var(--gray);
}

.payment-amount {
  font-size: 1.6rem;
  font-weight: 600;
}

.payment-body {
  padding: 1.5rem;
  min-height: 250px;
  display: flex;
  flex-direction: column;
}

.payment-body h3 {
  margin: 0 0 0.25rem;
  font-size: 1.25rem;
  font-weight: 600;
}

.payment-body p {
  margin: 0;
  color: var(--gray);
  font-size: 0.9rem;
}

.payment-error {
  margin-top: 1rem;
  padding: 0.75rem 1rem;
  border-radius: 0.5rem;
  background: #fdecea;
  color: #b02a37;
  font-size: 0.9rem;
}

.payment-info {
  margin-top: 1rem;
}

.payment-info-item {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.5rem 0;
  border-bottom: 1px solid var(--color-light-gray);
  font-size: 0.9rem;
}

.payment-info-item:last-child {
  border-bottom: 0;
}

.payment-info-item span {
  color: var(--gray);
}

.payment-info-item strong {
  font-weight: 500;
  text-align: right;
  word-break: break-all;
}

.status-paid {
  color: var(--color-success);
}

.payment-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 1.25rem;
}

.payment-button {
  min-width: 140px;
  width: auto;
  height: 40px;
  padding: 0 1.25rem;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
}

.payment-button-primary {
  background: var(--swich-blue);
  color: var(--color-white);
  border: 0;
}

.payment-button-alt {
  background: var(--color-white);
  color: var(--color-text-default);
  border: 1px solid var(--color-gray);
}

.payment-countdown {
  text-align: center;
  margin-top: 1rem;
}

.countdown-circle {
  width: 56px;
  height: 56px;
  margin: 0 auto 0.5rem;
  border-radius: 50%;
  border: 3px solid var(--swich-blue);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.4rem;
  font-weight: 600;
}

.payment-loader {
  margin: auto 0;
  text-align: center;
}

.spinner {
  width: 36px;
  height: 36px;
  margin: 0 auto 1rem;
  border: 4px solid var(--color-light-gray);
  border-top-color: var(--swich-blue);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.payment-footer {
  grid-column: 1 / -1;
  display: flex;
  justify-content: space-between;
  padding: 0.65rem 1.5rem;
  border-top: 1px solid var(--color-light-gray);
  font-size: 0.8rem;
  color: var(--gray);
}

@media screen and (max-width: 768px) {
  .payment-card {
    grid-template-columns: 1fr;
  }
}

@media screen and (max-width: 480px) {
  .payment-summary,
  .payment-body {
    padding: 1rem;
  }

  .payment-info-item {
    flex-direction: column;
    gap: 0.15rem;
  }

  .payment-info-item strong {
    text-align: left;
  }

  .payment-button {
    width: 100%;
  }
}
</style>
